import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLatest } from 'react-use'
import styled, { css } from 'styled-components'

import { Redacted } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { SegmentControl } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { reatomImportWallet } from '@root/features/wallet/import-wallet'
import { Input } from '@root/shared/ui-kit'

import type { DeviceType } from '@blade/ui-kit'
import type { ClassNameProps } from '@root/shared/react'
import type { Message } from 'react-hook-form'

type SecretType = 'phrase' | 'privateKey'

type Values = {
  name: string
  secret: string
}

type WalletImportProps = {
  onClose: () => void
  device: DeviceType
} & ClassNameProps

export const WalletImport: React.FC<WalletImportProps> = ({
  onClose,
  className,
  device = 'mobile',
}) => {
  const { t } = useTranslation()
  const [secretType, setSecretType] = useState<SecretType>('phrase')
  const onCloseRef = useLatest(onClose)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomImportWallet({
        onImported: () => onCloseRef.current(),
      }),
    [onCloseRef],
  )
  const [network] = useAtom(selectedNetworkAtom)
  const [requestState] = useAtom(model.importWalletStateAtom)
  const importWallet = useAction(model.importWalletAction)

  const elementHeight = device === 'mobile' ? 'm' : 'h36'

  const { handleSubmit, control, formState, trigger } = useForm<Values>({
    defaultValues: {
      name: '',
      secret: '',
    },
    mode: 'all',
  })

  useEffect(() => {
    trigger('secret')
  }, [secretType, trigger])

  const handleCancel = () => onCloseRef.current()
  const handleSubmitInternal = handleSubmit((values) => {
    importWallet({
      name: values.name,
      secret:
        secretType === 'phrase'
          ? {
              mnemonic: Redacted.make(
                values.secret.replaceAll(/\s+/g, ' ').trim().split(' '),
              ),
            }
          : { privateKey: Redacted.make(values.secret) },
    })
  })

  const validateSecret = (value: string): Message | undefined => {
    if (secretType === 'privateKey') {
      return undefined
    }

    const words = value.replaceAll(/\s+/g, ' ').trim().split(' ')
    return words.length > 11 ? undefined : t('common.invalid_input')
  }

  return (
    <Root onSubmit={handleSubmitInternal} className={className}>
      <Content>
        <Controller
          control={control}
          name="name"
          rules={{ minLength: 2, maxLength: 255, required: true }}
          render={({ field }) => (
            <Input
              startContent={t('wallet.wallet_name')}
              placeholder={t('wallet.import.name')}
              size={elementHeight}
              {...field}
            />
          )}
        />
        <ModeContainer>
          <SegmentControl value={secretType} onChange={setSecretType}>
            <ControlItem value={'phrase' satisfies SecretType}>
              {t('wallet.phrase')}
            </ControlItem>
            <ControlItem value={'privateKey' satisfies SecretType}>
              {t('wallet.private_key')}
            </ControlItem>
          </SegmentControl>
        </ModeContainer>

        <Label>
          {secretType === 'phrase'
            ? t('wallet.secret_phrase')
            : t('wallet.private_key')}
        </Label>
        <Controller
          control={control}
          name="secret"
          rules={{
            validate: validateSecret,
            required: true,
          }}
          render={({ field }) => (
            <TextArea
              {...field}
              value={field.value}
              onPaste={(e) => {
                if (secretType === 'phrase') {
                  const value = sanitizeMnemonic(
                    e.clipboardData.getData('text'),
                  )
                  field.onChange(value)
                  // Prevent duplication of the text in the clipboard
                  e.preventDefault()
                }
              }}
              placeholder={
                secretType === 'phrase'
                  ? t('wallet.import.enter_your_secret_phrase')
                  : t('wallet.import.enter_your_private_key')
              }
            />
          )}
        />

        {secretType === 'phrase' ? (
          <Description
            css={css`
              margin-top: 10px;
            `}
          >
            {network === Network.Ton
              ? t('wallet.import.phrase-hint-ton')
              : t('wallet.import.phrase_hint')}
          </Description>
        ) : undefined}
      </Content>
      <Actions>
        <Button
          variant="secondary"
          radius="rounded"
          size={elementHeight}
          onClick={handleCancel}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="solid"
          color="primary"
          radius="rounded"
          size={elementHeight}
          disabled={!formState.isValid}
          busy={requestState.loading}
        >
          {t('common.continue')}
        </Button>
      </Actions>
    </Root>
  )
}

const sanitizeMnemonic = (mnemonic: string) => {
  return mnemonic.replaceAll(/,|\s+/g, ' ').trim()
}

const Root = styled.form``

const Content = styled.div``

const TextArea = styled.textarea`
  border: 1px solid var(--backgroundA-3);
  background: var(--appearanceA-7);
  color: var(--text-10);

  border-radius: 12px;
  font-size: 11px;
  outline: none;

  width: 100%;
  height: 168px;
  font-weight: 500;
  resize: none;
  padding: 16px;

  &::placeholder {
    font-weight: 400;
    font-size: 11px;
    color: var(--text-7);
  }
`

const Label = styled.div`
  color: var(--text-11);

  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  margin-bottom: 8px;
`

const Description = styled.div`
  font-weight: 400;
  font-size: 12px;
  text-align: center;
  color: var(--text-10);
  opacity: 0.76;
`

const ModeContainer = styled.div`
  display: grid;
  justify-content: center;
  margin-block: 16px;
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`

const ControlItem = styled(SegmentControl.Item)`
  font-size: 11px;
  font-weight: 600;
`
